import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Loader2, Inbox, ExternalLink, Download } from "lucide-react";
import { useStudentAuth } from "../StudentAuth";
import {
  sOverview, sContent, sAnnouncements, sPayments, sProjects, sSubmitProject,
  sSubmitAssignment, sTickets, sCreateTicket, sUpdateProfile,
} from "../studentApi";
import { receiptUrl } from "@/lib/paymentApi";

const H1 = ({ title, sub }) => (
  <div className="mb-6"><h1 className="text-2xl font-bold text-slate-900">{title}</h1>{sub && <p className="text-sm text-slate-500">{sub}</p>}</div>
);
const Loading = () => <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div>;
const Empty = ({ text = "Nothing here yet." }) => (
  <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white py-16 text-slate-400"><Inbox className="h-9 w-9" /><p className="text-sm">{text}</p></div>
);
const Card = ({ children, testId }) => <div data-testid={testId} className="rounded-xl border border-slate-200 bg-white p-5">{children}</div>;
const LinkBtn = ({ href, label }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-royal hover:text-orange"><ExternalLink className="h-3.5 w-3.5" /> {label}</a>
);

const useFetch = (fn, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = () => { setLoading(true); fn().then((d) => setData(d)).catch(() => toast.error("Failed to load.")).finally(() => setLoading(false)); };
  useEffect(() => { reload(); }, deps);
  return { data, loading, reload };
};

const DashIcon = ({ n }) => { const C = Icons[n] || Icons.Circle; return <C className="h-5 w-5" />; };

/* DASHBOARD */
export function StudentDashboard() {
  const { data, loading } = useFetch(sOverview);
  if (loading) return <Loading />;
  const p = data?.profile || {};
  const cards = [
    { k: "materials", label: "Study Materials", icon: "BookOpen" },
    { k: "assignments", label: "Assignments", icon: "ClipboardList" },
    { k: "recordings", label: "Recorded Sessions", icon: "Video" },
    { k: "certificates", label: "Certificates", icon: "Award" },
  ];
  return (
    <div data-testid="student-dashboard">
      <H1 title={`Welcome, ${p.name || "Student"}`} sub="Your learning at a glance." />
      <Card testId="student-program-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">Enrolled Program</div>
            <div className="text-lg font-semibold text-slate-900">{p.enrolledProgram || "Not enrolled yet"}</div>
          </div>
          <div className="min-w-[200px] flex-1">
            <div className="mb-1 flex justify-between text-xs text-slate-500"><span>Progress</span><span>{p.progress || 0}%</span></div>
            <div className="h-2.5 w-full rounded-full bg-slate-200"><div className="h-2.5 rounded-full bg-orange" style={{ width: `${p.progress || 0}%` }} /></div>
          </div>
        </div>
      </Card>
      <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.k} testId={`student-stat-${c.k}`}>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-royal/10 text-royal"><DashIcon n={c.icon} /></span>
              <div><div className="text-xl font-bold text-slate-900">{data?.counts?.[c.k] ?? 0}</div><div className="text-xs text-slate-500">{c.label}</div></div>
            </div>
          </Card>
        ))}
      </div>
      <div className="mt-5">
        <h2 className="mb-3 font-semibold text-slate-800">Upcoming Classes</h2>
        {(!data?.upcomingClasses || data.upcomingClasses.length === 0) ? <Empty text="No upcoming classes scheduled yet." /> : (
          <div className="space-y-3">
            {data.upcomingClasses.map((c) => (
              <Card key={c.id}><div className="flex items-center justify-between gap-4"><div><div className="font-medium text-slate-900">{c.title}</div><div className="text-xs text-slate-500">{c.datetime || ""}</div></div>{c.link && <LinkBtn href={c.link} label="Join" />}</div></Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* PROFILE */
export function StudentProfile() {
  const { user, setUser } = useStudentAuth();
  const [f, setF] = useState(user || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(user || {}); }, [user]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { const u = await sUpdateProfile({ name: f.name, mobile: f.mobile, whatsapp: f.whatsapp, city: f.city, college: f.college, university: f.university, qualification: f.qualification, passingYear: f.passingYear }); setUser(u); toast.success("Profile updated."); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const fields = [["name", "Full Name"], ["mobile", "Mobile"], ["whatsapp", "WhatsApp Number"], ["city", "City"], ["college", "College"], ["university", "University"], ["qualification", "Qualification"], ["passingYear", "Passing Year"]];
  return (
    <div data-testid="student-profile" className="max-w-3xl">
      <H1 title="My Profile" sub="Manage your personal details." />
      <Card>
        <div className="mb-6 flex items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-navy text-2xl font-bold text-white">{(f.name || "S").slice(0, 1).toUpperCase()}</div>
          <div><div className="font-semibold text-slate-900">{f.email}</div><div className="text-xs text-slate-500">Profile photo upload coming soon</div></div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.map(([k, label]) => (
            <label key={k} className="flex flex-col gap-1.5 text-sm text-slate-600">{label}
              <input value={f[k] || ""} onChange={set(k)} data-testid={`profile-${k}`} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
            </label>
          ))}
          <label className="flex flex-col gap-1.5 text-sm text-slate-400">Email (locked)
            <input value={f.email || ""} disabled className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" />
          </label>
        </div>
        <button onClick={save} disabled={saving} data-testid="profile-save" className="mt-5 rounded-lg bg-orange px-6 py-2.5 font-medium text-white disabled:opacity-60">{saving ? "Saving…" : "Save changes"}</button>
      </Card>
    </div>
  );
}

/* PROGRAM */
export function StudentProgram() {
  const { user } = useStudentAuth();
  return (
    <div data-testid="student-program" className="max-w-3xl">
      <H1 title="My Program" sub="Your enrolled program and progress." />
      <Card>
        <div className="text-xs uppercase tracking-wide text-slate-400">Enrolled Program</div>
        <div className="text-2xl font-bold text-slate-900">{user?.enrolledProgram || "Not enrolled yet"}</div>
        {!user?.enrolledProgram && <p className="mt-2 text-sm text-slate-500">You are not enrolled in a program yet. Our team will enrol you once your admission is confirmed.</p>}
        <div className="mt-5"><div className="mb-1 flex justify-between text-xs text-slate-500"><span>Progress</span><span>{user?.progress || 0}%</span></div><div className="h-3 w-full rounded-full bg-slate-200"><div className="h-3 rounded-full bg-orange" style={{ width: `${user?.progress || 0}%` }} /></div></div>
      </Card>
    </div>
  );
}

/* GENERIC LIST (classes/materials/attendance/certificates) */
function ContentList({ type, title, sub, render, testId }) {
  const { data, loading } = useFetch(() => sContent(type), [type]);
  return (
    <div data-testid={testId}>
      <H1 title={title} sub={sub} />
      {loading ? <Loading /> : !data || data.length === 0 ? <Empty text="No items yet. Content will appear here once added by your mentor." /> : <div className="space-y-3">{data.map((item) => render(item))}</div>}
    </div>
  );
}
export const StudentClasses = () => (
  <ContentList type="classes" title="Classes" sub="Upcoming and scheduled live classes." testId="student-classes"
    render={(c) => <Card key={c.id}><div className="flex items-center justify-between gap-4"><div><div className="font-medium text-slate-900">{c.title}</div><div className="text-xs text-slate-500">{c.datetime || ""}</div></div>{c.link && <LinkBtn href={c.link} label="Join class" />}</div></Card>} />
);
export const StudentMaterials = () => (
  <ContentList type="materials" title="Study Materials" sub="Notes, slides and resources shared by your mentor." testId="student-materials"
    render={(m) => <Card key={m.id}><div className="flex items-center justify-between gap-4"><div><div className="font-medium text-slate-900">{m.title}</div>{m.description && <div className="text-xs text-slate-500">{m.description}</div>}</div>{m.link && <LinkBtn href={m.link} label="Open" />}</div></Card>} />
);
export const StudentAttendance = () => (
  <ContentList type="attendance" title="Attendance" sub="Your class attendance record (managed by ASAI)." testId="student-attendance"
    render={(a) => <Card key={a.id}><div className="flex items-center justify-between gap-4"><span className="text-sm text-slate-700">{a.date}</span><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${a.status === "Present" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{a.status}</span></div></Card>} />
);
export const StudentCertificates = () => (
  <ContentList type="certificates" title="Certificates" sub="Certificates issued by ASAI." testId="student-certificates"
    render={(c) => <Card key={c.id}><div className="flex items-center justify-between gap-4"><div><div className="font-medium text-slate-900">{c.title}</div><div className="text-xs text-slate-500">{c.issuedDate || ""}</div></div>{c.link && <LinkBtn href={c.link} label="View certificate" />}</div></Card>} />
);

/* ASSIGNMENTS */
export function StudentAssignments() {
  const { data, loading, reload } = useFetch(() => sContent("assignments"));
  const [active, setActive] = useState(null);
  const [link, setLink] = useState(""); const [note, setNote] = useState(""); const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!link.trim()) return toast.error("Enter a submission link.");
    setSaving(true);
    try { await sSubmitAssignment(active.id, { link, note }); toast.success("Assignment submitted."); setActive(null); setLink(""); setNote(""); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div data-testid="student-assignments">
      <H1 title="Assignments" sub="Complete and submit your assignments." />
      {loading ? <Loading /> : !data || data.length === 0 ? <Empty text="No assignments yet." /> : (
        <div className="space-y-3">{data.map((a) => (
          <Card key={a.id}><div className="flex items-center justify-between gap-4"><div><div className="font-medium text-slate-900">{a.title}</div>{a.description && <div className="text-xs text-slate-500">{a.description}</div>}{a.dueDate && <div className="mt-1 text-xs text-orange">Due: {a.dueDate}</div>}</div>
            <button onClick={() => setActive(a)} data-testid="assignment-submit-open" className="rounded-lg bg-orange px-4 py-2 text-sm font-medium text-white">Submit</button></div></Card>
        ))}</div>
      )}
      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setActive(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="assignment-modal">
            <h3 className="mb-4 text-lg font-bold text-slate-900">Submit: {active.title}</h3>
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Submission link (Drive/GitHub)" data-testid="assignment-link" className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" rows={3} data-testid="assignment-note" className="mb-4 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
            <button onClick={submit} disabled={saving} data-testid="assignment-submit" className="w-full rounded-lg bg-orange py-2.5 font-medium text-white disabled:opacity-60">{saving ? "Submitting…" : "Submit assignment"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* PROJECTS */
export function StudentProjects() {
  const { data, loading, reload } = useFetch(sProjects);
  const [f, setF] = useState({ title: "", link: "", note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.link.trim()) return toast.error("Enter a project link.");
    setSaving(true);
    try { await sSubmitProject(f); toast.success("Project submitted."); setF({ title: "", link: "", note: "" }); reload(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <div data-testid="student-projects" className="max-w-3xl">
      <H1 title="Project Submissions" sub="Submit your projects and track them." />
      <Card testId="project-form">
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input value={f.title} onChange={set("title")} placeholder="Project title" data-testid="project-title" className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
          <input value={f.link} onChange={set("link")} placeholder="Project link" data-testid="project-link" className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
          <textarea value={f.note} onChange={set("note")} placeholder="Note (optional)" data-testid="project-note" className="sm:col-span-2 rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" rows={2} />
          <button type="submit" disabled={saving} data-testid="project-submit" className="rounded-lg bg-orange px-5 py-2.5 font-medium text-white disabled:opacity-60 sm:col-span-2">{saving ? "Submitting…" : "Submit project"}</button>
        </form>
      </Card>
      <div className="mt-5">{loading ? <Loading /> : !data || data.length === 0 ? <Empty text="No projects submitted yet." /> : (
        <div className="space-y-3">{data.map((p) => <Card key={p.id}><div className="flex items-center justify-between gap-4"><div><div className="font-medium text-slate-900">{p.title}</div><div className="text-xs text-slate-500">{p.status} · {(p.submittedAt || "").slice(0, 10)}</div></div>{p.link && <LinkBtn href={p.link} label="Open" />}</div></Card>)}</div>
      )}</div>
    </div>
  );
}

/* PAYMENTS */
export function StudentPayments() {
  const { data, loading } = useFetch(sPayments);
  return (
    <div data-testid="student-payments">
      <H1 title="Payment History" sub="Your payments and receipts." />
      {loading ? <Loading /> : !data || data.length === 0 ? <Empty text="No payments found for your account." /> : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm"><thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Purpose</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Receipt</th></tr></thead>
          <tbody>{data.map((p) => (<tr key={p.paymentRef} className="border-t border-slate-100"><td className="px-4 py-3">{p.purposeLabel}</td><td className="px-4 py-3">₹{p.amountInr.toLocaleString("en-IN")}</td><td className="px-4 py-3">{p.status}</td><td className="px-4 py-3">{p.status === "Successful" ? <a href={receiptUrl(p.paymentRef)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-royal hover:text-orange"><Download className="h-4 w-4" /> PDF</a> : "—"}</td></tr>))}</tbody></table>
        </div>
      )}
    </div>
  );
}

/* ANNOUNCEMENTS */
export function StudentAnnouncements() {
  const { data, loading } = useFetch(sAnnouncements);
  return (
    <div data-testid="student-announcements">
      <H1 title="Announcements" sub="Latest updates from ASAI." />
      {loading ? <Loading /> : !data || data.length === 0 ? <Empty text="No announcements yet." /> : (
        <div className="space-y-3">{data.map((a) => <Card key={a.id}><div className="font-semibold text-slate-900">{a.title}</div><div className="mt-1 text-sm text-slate-600">{a.body}</div><div className="mt-2 text-xs text-slate-400">{(a.createdAt || "").slice(0, 10)}</div></Card>)}</div>
      )}
    </div>
  );
}

/* SUPPORT */
export function StudentSupport() {
  const { data, loading, reload } = useFetch(sTickets);
  const [f, setF] = useState({ subject: "", message: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.subject.trim() || !f.message.trim()) return toast.error("Subject and message are required.");
    setSaving(true);
    try { await sCreateTicket(f); toast.success("Ticket created."); setF({ subject: "", message: "" }); reload(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <div data-testid="student-support" className="max-w-3xl">
      <H1 title="Support" sub="Raise a support ticket and track responses." />
      <Card testId="ticket-form">
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input value={f.subject} onChange={set("subject")} placeholder="Subject" data-testid="ticket-subject" className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
          <textarea value={f.message} onChange={set("message")} placeholder="Describe your issue" rows={3} data-testid="ticket-message" className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
          <button type="submit" disabled={saving} data-testid="ticket-submit" className="rounded-lg bg-orange px-5 py-2.5 font-medium text-white disabled:opacity-60">{saving ? "Submitting…" : "Create ticket"}</button>
        </form>
      </Card>
      <div className="mt-5">{loading ? <Loading /> : !data || data.length === 0 ? <Empty text="No tickets yet." /> : (
        <div className="space-y-3">{data.map((t) => <Card key={t.id}><div className="flex items-center justify-between gap-4"><div><div className="font-medium text-slate-900">{t.subject}</div><div className="text-sm text-slate-600">{t.message}</div></div><span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{t.status}</span></div></Card>)}</div>
      )}</div>
    </div>
  );
}
